
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLanguage from 'utils/hooks/useLanguage';
import { LANGUAGES } from 'utils/Constants';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentRedaction } from 'store/AdminClient/RedactionData';
import { setAISmartContentType } from 'store/AdminClient/AISmartContentType';
import Joyride, { STATUS, EVENTS, ACTIONS } from "react-joyride";
import './Homepage.css';
import { adminClientOrderListSelector } from 'store/AdminClient/OrderList';
import { updateOrderDetails, deleteAllTexts } from 'store/AdminClient/OrderDisplay';
import { fetchAdminClientAllOrders } from 'store/AdminClient/OrderList/features';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import { UserSessionSelector } from 'store/UserSession';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import WebsiteFilter from 'components/shared/WebsiteFilter';
import axiosPrivate from 'config/axiosPrivate';
import axiosSeoOverview from 'config/axiosSeoOverview';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import { TrafficPerformanceChart } from 'components/shared/TrafficPerformanceChart/TrafficPerformanceChart';
import { useGuide } from "utils/contexts/GuideContext";
import { formatDateShort } from 'utils/DateUtils';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_DASHBOARD_PAGE = '/AdminClient/homepage';

export const AdminClientHomePage = () => {
  usePageTracking(ADMIN_CLIENT_DASHBOARD_PAGE);

  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const dispatch = useDispatch();

  // Fonctions pour obtenir le label et  'icône du statut d'un texte (comme dans ProjectTextsTable)
  const getTextStatusLabel = (content) => {
    if (content?.deployed) return t('En ligne');
    if (content?.validated) return t('Validé');
    if (content?.inRevision) return t('En révision');
    if (content?.delivered) return t('Livré');
    return t('Brouillon');
  };

  const getTextStatusIcon = (content) => {
    if (content?.deployed) return '/Images/deployed-text-icon.png';
    if (content?.validated) return '/Images/validate-text-icon.png';
    if (content?.inRevision) return '/Images/en_revision.png';
    if (content?.delivered) return '/Images/delivred-text-icon.png';
    return '/Images/drafted-text-icon.png';
  };

  const getProjectStatusLabel = (project) => {
    const status = project?.status;
    if (!status) return '-';
    const normalizedStatus = String(status).toUpperCase().trim();
    switch (normalizedStatus) {
      case 'IN_PROGRESS':
        return t('En cours');
      case 'DELAYED':
        return t('En retard');
      case 'VALIDATED':
        return t('Validé');
      case 'NON_ATTRIBUTED':
        return t('Non attribué');
      default:
        return status;
    }
  };

  const getProjectStatusIcon = (project) => {
    const status = project?.status;
    if (!status) return '/Images/drafted-text-icon.png';
    const normalizedStatus = String(status).toUpperCase().trim();
    switch (normalizedStatus) {
      case 'IN_PROGRESS':
        return '/Images/delivred-text-icon.png';
      case 'DELAYED':
        return '/Images/drafted-text-icon.png';
      case 'VALIDATED':
        return '/Images/validate-text-icon.png';
      case 'NON_ATTRIBUTED':
        return '/Images/drafted-text-icon.png';
      default:
        return '/Images/drafted-text-icon.png';
    }
  };
  const [urlSearch, setUrlSearch] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const websiteSelectRef = useRef(null);
  const history = useHistory();
  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');
  const [isDeleteWithProjectsFlow, setIsDeleteWithProjectsFlow] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState(null);
  const [companyQuotas, setCompanyQuotas] = useState(null);
  const [isWebsiteLimitModalOpen, setIsWebsiteLimitModalOpen] = useState(false);
  const [latestContents, setLatestContents] = useState([]);
  const [isLoadingContents, setIsLoadingContents] = useState(false);
  const [latestProjects, setLatestProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [donutTooltip, setDonutTooltip] = useState({ visible: false, label: '', x: 0, y: 0 });
  const [projectStats, setProjectStats] = useState({
    numberOfProjectUnassigned: 0,
    numberOfProjectInProgress: 0,
    numberOfProjectInDelay: 0,
    numberOfProjectValidated: 0,
    numberOfProjects: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [contentStats, setContentStats] = useState({
    numberOfTextInDraft: 0,
    numberOfTextDelivered: 0,
    numberOfTextInRevision: 0,
    numberOfTextDeployed: 0,
    numberOfTextValidated: 0,
    numberOfTextValidatedAndNotDeployed: 0,
    averageNumberOfTextDeployedPerMonth: 0,
    averageNumberOfTextDeployedPerYear: 0,
    averageNumberOfTextDeliveredPerMonth: 0,
    averageNumberOfTextDeliveredPerYear: 0,
    averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
    totalNumberOfText: 0
  });
  const [isLoadingContentStats, setIsLoadingContentStats] = useState(false);
  const [monthlyTextsStats, setMonthlyTextsStats] = useState({ delivered: {}, deployed: {} });
  const [isLoadingMonthlyStats, setIsLoadingMonthlyStats] = useState(false);
  const [seoConfig, setSeoConfig] = useState(null);
  const [isLoadingSeoConfig, setIsLoadingSeoConfig] = useState(false);
  const [topContentsData, setTopContentsData] = useState([]);
  const [isLoadingTopContents, setIsLoadingTopContents] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const yearDropdownRef = useRef(null);
  const yearButtonRef = useRef(null);
  const normalizedPlanKey = String(companyQuotas?.planKey || 'free').trim().toLowerCase();
  const isFreePlan = normalizedPlanKey === 'free';


  const availableYears = useMemo(() => {
    const yearsSet = new Set();


    if (monthlyTextsStats?.deployed && typeof monthlyTextsStats.deployed === 'object') {
      Object.keys(monthlyTextsStats.deployed).forEach(year => {
        if (year && !isNaN(Number(year))) {
          yearsSet.add(Number(year));
        }
      });
    }


    const years = Array.from(yearsSet).sort((a, b) => b - a);

    return years.length > 0 ? years : [new Date().getFullYear()];
  }, [monthlyTextsStats]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);
  const handleEditWebsite = (website) => {
    try {
      if (website?.id) {
        localStorage.setItem('editWebsiteId', String(website.id));
      }
      history.push('/AdminClient/AjouterSiteWeb');
    } catch (e) { }
  };
  const handleAddWebsite = () => {
    try {
      const maxWebsites = Number(companyQuotas?.maxWebsites);
      const currentWebsites = Number(companyQuotas?.currentWebsites);
      const hasQuotaValues = Number.isFinite(maxWebsites) && Number.isFinite(currentWebsites);
      const canAddWebsite = !hasQuotaValues || (maxWebsites - currentWebsites > 0);

      if (!canAddWebsite) {
        setIsWebsiteLimitModalOpen(true);
        return;
      }
      localStorage.removeItem('editWebsiteId');
      history.push('/AdminClient/AjouterSiteWeb');
    } catch (e) { }
  };
  const handleDeleteWebsite = (website) => {
    setWebsiteToDelete(website || null);
    setIsDeleteWithProjectsFlow(false);
    setConfirmationMsg(
      `${t('Voulez-vous vraiment supprimer')} ${website?.urlSite || ''} ?`
    );
    setConfirmation(true);
  };
  const openProjectDetails = (project) => {
    try {
      if (project?.id) {
        window.localStorage.setItem('AdminClient_ProjectDisplay_id', String(project.id));
        dispatch(updateOrderDetails({}));
        dispatch(deleteAllTexts());
        history.push('/AdminClient/DetailsProject');
      }
    } catch (e) { }
  };
  const [runTour, setRunTour] = useState(false);
  const { setGuideHandler } = useGuide();
  const [domReady, setDomReady] = useState(false);
  useEffect(() => {
    const alreadySeen = localStorage.getItem("guide_dashboard_seen") === "1";

    if (!alreadySeen) {
      setTimeout(() => setRunTour(true), 800);
    }
  }, []);
  useEffect(() => {
    setGuideHandler(() => startTour);
  }, [setGuideHandler]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        yearDropdownRef.current &&
        yearButtonRef.current &&
        !yearButtonRef.current.contains(event.target) &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        yearDropdownRef.current.classList.remove('year-dropdown-open');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);




  const steps = [
    {
      target: "#website-filter-container",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("🎯 Commencez ici")}</h5>
          <p>
            {t("Configurez votre workspace en ajoutant votre siteweb.")} <br />
            {t("Vous pourrez ensuite créer et suivre vos contenus, configurer vos agents, gérer vos traductions et piloter vos projets.")} <br />
          </p>
        </div>
      ),
      disableBeacon: true,
    },



    // --- Menu latéral ---

    {
      target: "#agent-IA",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("🤖 Agents IA")}</h5>
          <p>
            {t("Configurez vos agents IA pour automatiser la création et la traduction de vos contenus, selon vos propres règles éditoriales.")}
          </p>
          <p>{t("Dans cette section, vous pouvez paramétrer le ton, le style et ajouter des consignes afin d’obtenir des résultats adaptés à votre marque et à chaque type de contenu.")}<br /><b>{t("Résultat")} :</b> {t("Vous gagnez du temps sur la création/traduction de contenu, tout en gardant le contrôle sur la qualité et la pertinence des productions.")}</p>
        </div>
      ),
      placement: "right",
    },

    {
      target: "#IA-Generator",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("✍️ Rédaction IA")}</h5>
          <p>
            {t("Accédez aux générateurs IA pour créer différents types de contenus : fiches produit, fiches GMB, pages catégories et bien plus.")}
          </p>
          <p>{t("Cette section vous permet de tester vos agents IA et de comparer les LLM disponibles selon chaque type de contenu.")}
            <br />{t("Vous pouvez ainsi évaluer la qualité des résultats, ajuster vos paramètres et affiner vos choix avant de lancer une production à grande échelle.")}
            <br />{t("C’est également l’endroit idéal pour générer des contenus à l’unité, de manière rapide et contrôlée.")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },

    {
      target: "#IA-translate",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("🌍 Traduction IA")}</h5>
          <p>
            {t("Traduisez vos contenus en plusieurs langues en quelques clics, tout en préservant leur cohérence SEO et leur qualité éditoriale.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },

    {
      target: "#project-menu",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📂 Gestion des projets")}</h5>
          <p>
            {t("Lancez un nouveau projet en quelques secondes en définissant vos objectifs, le mode de production et les types de contenus à produire.")}
          </p>
          <p>
            {t("Cette section vous permet de centraliser la production, de garder une vision claire de vos contenus en cours et d’optimiser votre organisation pour produire plus efficacement.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },

    {
      target: ".sb-item[href='/AdminClient/AdminMainMenu']",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("🏠 Tableau de bord")}</h5>
          <p>
            {t("Piloter l’ensemble de votre activité en un coup d’œil.")} <br />
            {("Accédez à vos indicateurs clés, consultez vos contenus récents et suivez l’avancement de vos projets")}
          </p>
        </div>
      ),
    },
    {
      target: "#latest-contents-section",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📝 Derniers contenus créés")}</h5>
          <p>
            {t("Retrouvez ici les derniers contenus créés pour le site sélectionné.")} <br />
            {t("Cliquez sur un texte pour consulter sa fiche détaillée.")}
          </p>
        </div>
      ),
    },

    {
      target: "#latest-project-section",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📁 Derniers projets créés")}</h5>
          <p>
            {t("Accédez rapidement aux derniers projets lancés pour ce site.")} <br />
            {t("Cliquez sur un projet pour voir son avancement et les contenus associés.")}
          </p>
        </div>
      ),
    },

    {
      target: ".Performances",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📊 Suivi de vos projets")}</h5>
          <p>
            {t("Visualisez la répartition de vos projets : en cours, en retard ou validés.")} <br />
            {t("Une vue d’ensemble claire pour suivre l’état global de votre production.")}
          </p>
        </div>
      ),
    },

    {
      target: ".second-card",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📑 Statut d’avancement des contenus")}</h5>
          <p>
            {t("Suivez ici la répartition de vos contenus : brouillons, livrés et publiés.")} <br />
            {t("Idéal pour identifier rapidement où concentrer vos efforts.")}
          </p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
    },

    {
      target: "#monthly-stat",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📅 Textes publiés par mois")}</h5>
          <p>
            {t("Visualisez le nombre de textes publiés chaque mois pour le site sélectionné.")} <br />
            {t("Suivez ainsi votre rythme de déploiement dans le temps.")}
          </p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
    },

    {
      target: "#topRanked",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("⭐ Top mots-clés")}</h5>
          <p>
            {t("Découvrez vos 5 mots-clés les plus performants issus de la Search Console.")} <br />
            {t("Comparez leur position, les clics actuels et les clics de l’année précédente.")}
          </p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
    },

    {
      target: "#evolution-performance",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📈 Activer le suivi du trafic")}</h5>
          <p>
            {t("Ajoutez notre adresse e-mail à votre Search Console pour activer cette analyse.")} <br />
            {t("Vous pourrez ensuite suivre l’évolution de votre trafic directement dans Typix.")}
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },

    {
      target: "#user-management",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("🧑‍🤝‍🧑 Gestion des utilisateurs")}</h5>
          <p>
            {t("Invitez de nouveaux membres, gérez leurs droits et organisez vos équipes simplement.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#profil",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("👤 Mon profil")}</h5>
          <p>
            {t("Modifiez vos informations personnelles.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },

    {
      target: "#support",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("📩 Contactez notre équipe")}</h5>
          <p>
            {t("Besoin d’aide ? Envoyez-nous un message et nous reviendrons vers vous rapidement pour vous assister")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#guide",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("❓ Mon guide")}</h5>
          <p>
            {t("Relancez ce guide interactif à tout moment pour retrouver les principales fonctionnalités de Typix.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
  ];


  const handleJoyrideCallback = (data) => {
    console.log("JOY RIDE EVENT =>", data);
    const { status, action, type } = data;

    // 👉 Si on clique sur le X (action CLOSE) ou sur l'overlay
    if (action === ACTIONS.CLOSE || type === EVENTS.OVERLAY) {
      setRunTour(false);
      localStorage.setItem("guide_dashboard_seen", "1");
      return;
    }

    // 👉 Quand le tour est terminé ou passé avec "Passer"
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      localStorage.setItem("guide_dashboard_seen", "1");
    }
  };

  const startTour = () => {
    setRunTour(false);
    setTimeout(() => setRunTour(true), 0);
  };
  useEffect(() => {
    console.log("Existe ?", document.querySelector("#guide-select-website"));
  }, []);
  const openTextDetails = async (content) => {
    try {
      if (content?.contentType) {
        localStorage.setItem('AdminClient_TextId', content.id);
        dispatch(setAISmartContentType(content.contentType));
        history.push(`/AdminClient/createIAsmarttext/${content.id}`);
        return;
      }

      let d = null;
      try {
        const res = await axiosPrivate.get(`/texts/external/${content?.id}/fullDetails`);
        d = res?.data || null;
      } catch (e) { }

      const normalizedType = String(d?.type || '');
      const projectId = d?.projectId ?? null;
      if (normalizedType === 'TRANSLATED' && !projectId) {
        localStorage.setItem('AdminClient_TextId', content.id);
        history.push(`/AdminClient/CreateTraductionIA/${content.id}`);
        return;
      }

      const clientsList = Array.isArray(clients)
        ? clients
        : clients?.websites || clients?.data || [];
      const selectedWebsite =
        clientsList.find(
          (c) =>
            c?.urlSite === urlSearch ||
            c?.url === urlSearch ||
            c?.name === urlSearch
        ) || null;

      dispatch(
        setCurrentRedaction({
          id: (d && d.id) || content?.id,
          type: (d && d.type) || '',
          textType: (d && d.type) || '',
          title: (d && (d.textTitle || d.keyword)) || content?.keyword || '',
          textTitle: (d && (d.textTitle || d.keyword)) || content?.keyword || '',
          keyword: (d && d.keyword) || content?.keyword || '',
          url: (d && d.url) || '',
          titleTag: (d && d.titleTag) || '',
          metaDescriptionTag: (d && d.metaDescriptionTag) || '',
          initialHtmlContent: (d && d.initialHtmlContent) || '',
          initialTextContent: (d && d.initialTextContent) || '',
          actualHtmlContent: (d && d.actualHtmlContent) || '',
          actualTextContent: (d && d.actualTextContent) || '',
          semanticGap: (d && d.semanticGap) || [],
          analyseResult: (d && d.analyseResult) || null,
          stuffedKeywords: (d && d.stuffedKeywords) || [],
          internalLinking: (d && d.internalLinking) || '',
          numberWords: Number(d?.numberWords) || 0,
          axeRedaction: (d && (d.axeRedaction || d.writingAxis)) || '',
          retranscription: (d && d.retranscription) || '',
          delivered: !!(d && d.delivered),
          inRevision: !!(d && d.inRevision),
          revisionSent: !!(d && d.revisionSent),
          revisionDone: !!(d && d.revisionDone),
          globalRevisionSent: !!(d && d.globalRevisionSent),
          validated: !!(d && d.validated),
          orderId: (d && d.projectId) || null,
          projectId: (d && d.projectId) || null,
          websiteId: (d && d.websiteId) || selectedWebsite?.id || null,
          country: selectedWebsite?.country || null,
          orderWebsite:
            selectedWebsite?.urlSite ||
            selectedWebsite?.url ||
            '',
          createdAt: (d && d.createdAt) || content?.createdAt || new Date().toISOString(),
          initialScoreData: (d && d.initialScoreData) || null,
          contentScore: (d && d.contentScore) || null,
        })
      );

      const textId = (d && d.id) || content?.id;
      try {
        if (textId) localStorage.setItem('AdminClient_TextId', String(textId));
      } catch (e) { }
      history.push(textId ? `/AdminClient/TextDetails/${textId}` : '/AdminClient/TextDetails');
    } catch (e) { }
  };
  const onDeleteWebsiteSuccess = () => {
    setConfirmation(false);
    setIsDeleteWithProjectsFlow(false);
    setIsSuccess(true);
    setErrorSuccessMsg(`${t('Le site web')} ${websiteToDelete?.urlSite || ''} ${t('a été supprimé avec succès')} !`);
    setErrorSuccess(true);
    dispatch(fetchAdminClientClientList());
    try {
      if (String(websiteToDelete?.id) === localStorage.getItem('editWebsiteId')) {
        localStorage.removeItem('editWebsiteId');
      }
    } catch (e) { }
  };

  const deleteWebsite = async () => {
    if (!websiteToDelete?.id) {
      setConfirmation(false);
      setIsDeleteWithProjectsFlow(false);
      return;
    }
    try {
      await axiosPrivate.delete(`/project/manager/website/${websiteToDelete.id}`);
      onDeleteWebsiteSuccess();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        setIsDeleteWithProjectsFlow(true);
        setConfirmationMsg(
          t('Vous êtes sur le point de supprimer ce site et tous les projets qui y sont liés. Cette action est irréversible.')
        );
        setConfirmation(true);
        return;
      }
      setConfirmation(false);
      setIsDeleteWithProjectsFlow(false);
      setIsSuccess(false);
      setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
      setErrorSuccess(true);
    }
  };

  const deleteWebsiteWithProjects = async () => {
    if (!websiteToDelete?.id) {
      setConfirmation(false);
      setIsDeleteWithProjectsFlow(false);
      return;
    }
    try {
      await axiosPrivate.delete(`/project/manager/website/${websiteToDelete.id}/with-projects`);
      onDeleteWebsiteSuccess();
    } catch (e) {
      setConfirmation(false);
      setIsDeleteWithProjectsFlow(false);
      setIsSuccess(false);
      setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
      setErrorSuccess(true);
    }
  };
  const {
    inProgressOrders = [],
    delayedOrders = [],
    validatedOrders = [],
    nonAttributedOrders = [],
    smartOrders = [],
    smartWithReviewOrders = [],
    isLoading = false,
  } = useSelector(adminClientOrderListSelector);
  const { user } = useSelector(UserSessionSelector) || {};
  const { clients = [], isLoadingClients } =
    useSelector(adminClientClientListSelector) || {};
  useEffect(() => {
    try {
      window.localStorage.removeItem('AdminClient_ProjectDisplay_id');
      window.sessionStorage.removeItem('AdminClient_ProjectDisplay_fromCategory');
    } catch { }
    document.title = t('Typix - Mon tableau de bord');
    window.dispatchEvent(new Event('locationchange'));
    dispatch(fetchAdminClientAllOrders());
    dispatch(fetchAdminClientClientList());

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        setCompanyQuotas(data || null);
      } catch (error) {
        setCompanyQuotas(null);
      }
    };
    fetchCompanyQuotas();
  }, [dispatch, t]);


  useEffect(() => {
    if (!urlSearch && !isLoadingClients && clients) {
      const rawList = Array.isArray(clients)
        ? clients
        : clients?.websites || clients?.data || [];

      const firstWebsite = rawList.find(
        (c) => c?.urlSite || c?.url || c?.name
      );

      if (firstWebsite) {
        const websiteUrl = firstWebsite.urlSite || firstWebsite.url || firstWebsite.name;
        if (websiteUrl) {
          setUrlSearch(websiteUrl);
        }
      }
    }
  }, [urlSearch, clients, isLoadingClients, setUrlSearch]);

  useEffect(() => {
    const fetchSeoConfig = async () => {
      setIsLoadingSeoConfig(true);
      try {
        const response = await axiosSeoOverview.get('/settings/get_all_config');
        const configs = response?.data || [];
        const validConfigs = Array.isArray(configs)
          ? configs.filter(config => config?.account && config.account !== 'new1')
          : [];
        setSeoConfig(validConfigs);
      } catch (error) {
        setSeoConfig([]);
      } finally {
        setIsLoadingSeoConfig(false);
      }
    };

    fetchSeoConfig();
  }, []);

  useEffect(() => {
    const fetchLatestContents = async () => {
      if (!urlSearch) {
        setLatestContents([]);
        return;
      }

      const rawList = Array.isArray(clients)
        ? clients
        : clients?.websites || clients?.data || [];
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setLatestContents([]);
        return;
      }

      setIsLoadingContents(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/by-website/${selectedWebsite.id}`
        );
        const contents = response?.data || [];
        const sortedContents = Array.isArray(contents)
          ? contents.sort(
            (a, b) =>
              new Date(b.createdAt || b.dateCreation) -
              new Date(a.createdAt || a.dateCreation)
          )
          : [];
        // On garde toute la liste triée, la limite (5) est gérée dans le rendu
        setLatestContents(sortedContents);
      } catch (error) {
        setLatestContents([]);
      } finally {
        setIsLoadingContents(false);
      }
    };

    fetchLatestContents();
  }, [urlSearch, clients]);

  useEffect(() => {
    const fetchMonthly = async () => {
      if (!urlSearch) {
        setMonthlyTextsStats({ delivered: {}, deployed: {} });
        return;
      }
      const rawList = Array.isArray(clients) ? clients : clients?.websites || clients?.data || [];
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );
      if (!selectedWebsite?.id) {
        setMonthlyTextsStats({ delivered: {}, deployed: {} });
        return;
      }
      setIsLoadingMonthlyStats(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/stats/by-website/per-month/${selectedWebsite.id}`
        );
        const data = response?.data || {};
        setMonthlyTextsStats({
          delivered: data?.delivered || {},
          deployed: data?.deployed || {},
        });
      } catch (e) {
        setMonthlyTextsStats({ delivered: {}, deployed: {} });
      } finally {
        setIsLoadingMonthlyStats(false);
      }
    };
    fetchMonthly();
  }, [urlSearch, clients]);


  useEffect(() => {
    const fetchLatestProjects = async () => {
      if (!urlSearch) {
        setLatestProjects([]);
        return;
      }

      const rawList = Array.isArray(clients)
        ? clients
        : clients?.websites || clients?.data || [];
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setLatestProjects([]);
        return;
      }

      setIsLoadingProjects(true);
      try {
        const response = await axiosPrivate.get(
          `/project/by-website/last-five-in-progress/${selectedWebsite.id}`
        );
        const projects = response?.data || [];
        setLatestProjects(Array.isArray(projects) ? projects : []);
      } catch (error) {
        setLatestProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchLatestProjects();
  }, [urlSearch, clients]);

  useEffect(() => {
    const fetchProjectStats = async () => {
      if (!urlSearch) {
        setProjectStats({
          numberOfProjectUnassigned: 0,
          numberOfProjectInProgress: 0,
          numberOfProjectInDelay: 0,
          numberOfProjectValidated: 0,
          numberOfProjects: 0
        });
        return;
      }

      const rawList = Array.isArray(clients)
        ? clients
        : clients?.websites || clients?.data || [];
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setProjectStats({
          numberOfProjectUnassigned: 0,
          numberOfProjectInProgress: 0,
          numberOfProjectInDelay: 0,
          numberOfProjectValidated: 0,
          numberOfProjects: 0
        });
        return;
      }

      setIsLoadingStats(true);
      try {
        const response = await axiosPrivate.get(
          `/project/stats/by-website/${selectedWebsite.id}`
        );
        const stats = response?.data || {};
        setProjectStats({
          numberOfProjectUnassigned: stats.numberOfProjectUnassigned || 0,
          numberOfProjectInProgress: stats.numberOfProjectInProgress || 0,
          numberOfProjectInDelay: stats.numberOfProjectInDelay || 0,
          numberOfProjectValidated: stats.numberOfProjectValidated || 0,
          numberOfProjects: stats.numberOfProjects || 0
        });
      } catch (error) {
        setProjectStats({
          numberOfProjectUnassigned: 0,
          numberOfProjectInProgress: 0,
          numberOfProjectInDelay: 0,
          numberOfProjectValidated: 0,
          numberOfProjects: 0
        });
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchProjectStats();
  }, [urlSearch, clients]);

  useEffect(() => {
    const fetchContentStats = async () => {
      if (!urlSearch) {
        setContentStats({
          numberOfTextInDraft: 0,
          numberOfTextDelivered: 0,
          numberOfTextInRevision: 0,
          numberOfTextDeployed: 0,
          numberOfTextValidated: 0,
          numberOfTextValidatedAndNotDeployed: 0,
          averageNumberOfTextDeployedPerMonth: 0,
          averageNumberOfTextDeployedPerYear: 0,
          averageNumberOfTextDeliveredPerMonth: 0,
          averageNumberOfTextDeliveredPerYear: 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
          totalNumberOfText: 0
        });
        return;
      }

      const rawList = Array.isArray(clients)
        ? clients
        : clients?.websites || clients?.data || [];
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setContentStats({
          numberOfTextInDraft: 0,
          numberOfTextDelivered: 0,
          numberOfTextInRevision: 0,
          numberOfTextDeployed: 0,
          numberOfTextValidated: 0,
          numberOfTextValidatedAndNotDeployed: 0,
          averageNumberOfTextDeployedPerMonth: 0,
          averageNumberOfTextDeployedPerYear: 0,
          averageNumberOfTextDeliveredPerMonth: 0,
          averageNumberOfTextDeliveredPerYear: 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
          totalNumberOfText: 0
        });
        return;
      }

      setIsLoadingContentStats(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/stats/by-website/${selectedWebsite.id}`
        );
        const stats = response?.data || {};
        setContentStats({
          numberOfTextInDraft: stats.numberOfTextInDraft || 0,
          numberOfTextDelivered: stats.numberOfTextDelivered || 0,
          numberOfTextInRevision: stats.numberOfTextInRevision || 0,
          numberOfTextDeployed: stats.numberOfTextDeployed || 0,
          numberOfTextValidated: stats.numberOfTextValidated || 0,
          numberOfTextValidatedAndNotDeployed: stats.numberOfTextValidatedAndNotDeployed || 0,
          averageNumberOfTextDeployedPerMonth: stats.averageNumberOfTextDeployedPerMonth || 0,
          averageNumberOfTextDeployedPerYear: stats.averageNumberOfTextDeployedPerYear || 0,
          averageNumberOfTextDeliveredPerMonth: stats.averageNumberOfTextDeliveredPerMonth || 0,
          averageNumberOfTextDeliveredPerYear: stats.averageNumberOfTextDeliveredPerYear || 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: stats.averageNumberOfProjectWhereAllTextDeliveredPerYear || 0,
          totalNumberOfText: stats.totalNumberOfText || 0
        });
      } catch (error) {
        setContentStats({
          numberOfTextInDraft: 0,
          numberOfTextDelivered: 0,
          numberOfTextInRevision: 0,
          numberOfTextDeployed: 0,
          numberOfTextValidated: 0,
          numberOfTextValidatedAndNotDeployed: 0,
          averageNumberOfTextDeployedPerMonth: 0,
          averageNumberOfTextDeployedPerYear: 0,
          averageNumberOfTextDeliveredPerMonth: 0,
          averageNumberOfTextDeliveredPerYear: 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
          totalNumberOfText: 0
        });
      } finally {
        setIsLoadingContentStats(false);
      }
    };

    fetchContentStats();
  }, [urlSearch, clients]);

  useEffect(() => {
    const fetchTopContents = async () => {
      if (!urlSearch || !seoConfig || seoConfig.length === 0) {
        setTopContentsData([]);
        return;
      }

      const normalizeUrl = (url) => {
        if (!url) return '';
        return url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
      };

      const normalizedSearch = normalizeUrl(urlSearch);


      const matchingConfig = seoConfig.find(config => {
        const normalizedWebsiteName = normalizeUrl(config?.websiteName);
        return normalizedWebsiteName === normalizedSearch;
      });

      if (!matchingConfig || !matchingConfig.activation) {
        setTopContentsData([]);
        return;
      }

      const { account, websiteId } = matchingConfig;

      if (account && websiteId) {
        setIsLoadingTopContents(true);
        try {
          const positionResponse = await axiosSeoOverview.get(
            `/sc/aggregated-search-console-query`,
            {
              params: {
                account: account,
                websiteId: websiteId,
                dimension: 'Position VS. M-1'
              }
            }
          );

          const clicksResponse = await axiosSeoOverview.get(
            `/sc/aggregated-search-console-query`,
            {
              params: {
                account: account,
                websiteId: websiteId,
                dimension: 'Clicks VS. Y-1'
              }
            }
          );

          const positionData = positionResponse?.data || [];
          const clicksData = clicksResponse?.data || [];

          const clicksMap = new Map();
          clicksData.forEach(item => {
            if (item?.query) {
              clicksMap.set(item.query, {
                currentValue: item.rawCurrentValue,
                previousValue: item.previousValue
              });
            }
          });

          const mergedData = positionData.map(posItem => {
            const clicksInfo = clicksMap.get(posItem?.query) || {};
            return {
              query: posItem.query,
              currentPosition: posItem.currentPosition,
              currentValue: clicksInfo.currentValue,
              previousValue: clicksInfo.previousValue
            };
          });

          const sortedData = mergedData
            .filter(item => item.currentValue !== null && item.currentValue !== undefined)
            .sort((a, b) => (b.currentValue || 0) - (a.currentValue || 0));

          setTopContentsData(sortedData);
        } catch (error) {
          setTopContentsData([]);
        } finally {
          setIsLoadingTopContents(false);
        }
      }
    };

    fetchTopContents();
  }, [urlSearch, seoConfig]);

  const totalProjects = projectStats.numberOfProjects || 0;
  const unassignedPct =
    totalProjects > 0
      ? Math.round((projectStats.numberOfProjectUnassigned / totalProjects) * 100)
      : 0;
  const inProgressPct =
    totalProjects > 0
      ? Math.round((projectStats.numberOfProjectInProgress / totalProjects) * 100)
      : 0;
  const delayedPct =
    totalProjects > 0
      ? Math.round((projectStats.numberOfProjectInDelay / totalProjects) * 100)
      : 0;
  const validatedPct =
    totalProjects > 0
      ? Math.round((projectStats.numberOfProjectValidated / totalProjects) * 100)
      : 0;

  const unassignedPlusInProgress = Math.min(unassignedPct + inProgressPct, 100);
  const unassignedPlusInProgressPlusDelay = Math.min(
    unassignedPct + inProgressPct + delayedPct,
    100
  );
  const totalPct = Math.min(
    unassignedPct + inProgressPct + delayedPct + validatedPct,
    100
  );
  const centerPct = totalProjects > 0 ? Math.round(totalPct) : 0;
  const radius = 50;
  const circumference = 2 * Math.PI * radius;

  const safePct = (v) => (Number.isFinite(v) && v > 0 ? Math.min(v, 100) : 0);
  const uPct = safePct(unassignedPct);
  const pPct = safePct(inProgressPct);
  const dPct = safePct(delayedPct);
  const vPct = safePct(validatedPct);

  const uLen = (uPct / 100) * circumference;
  const pLen = (pPct / 100) * circumference;
  const dLen = (dPct / 100) * circumference;
  const vLen = (vPct / 100) * circumference;


  const segments = [];
  if (uLen > 0) segments.push({ color: '#6458F1', length: uLen, label: t('Non attribué') });   // Non attribué
  if (pLen > 0) segments.push({ color: '#FF855D', length: pLen, label: t('En cours') });   // En cours
  if (dLen > 0) segments.push({ color: '#C06DD8', length: dLen, label: t('En retard') });   // En retard
  if (vLen > 0) segments.push({ color: '#63D3D7', length: vLen, label: t('Validé') });   // Validé

  return (

    <div className="content-center admin-home-page">
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks={true}
        spotlightPadding={5}
        disableOverlayClose={false}
        disableCloseOnEsc={false}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            zIndex: 1000,
            primaryColor: "#0064d2",
          },
          overlay: {
            zIndex: 1000,
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
          spotlight: {
            zIndex: 1001,             // trou autour de l’élément ciblé
            borderRadius: 8,
            boxShadow: "0 0 0 3px rgba(0, 100, 210, 0.25)",
          },
          tooltip: {
            zIndex: 1002,
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            width: "auto",
            maxWidth: "none",        // <-- IMPORTANT
            display: "inline-block", // <-- permet au bloc de s'ajuster au texte
          },
          tooltipContainer: {
            textAlign: "left",
          },
          tooltipTitle: {
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 8,
          },

          buttonNext: {
            borderRadius: 999,
            padding: "6px 14px",
            fontWeight: 600,
            fontSize: 13,
          },
          buttonBack: {
            borderRadius: 999,
            padding: "6px 10px",
            fontSize: 12,
          },
          buttonSkip: {
            fontSize: 12,
          },
        }}
        locale={{
          last: t('Terminer'),
          next: t('Suivant'),
          back: t('Précédent'),
          skip: t('Passer'),
          close: t('Fermer'),
        }}
      />



      <div className="pro-header">
        <div className="pro-header__inner">
          {/* Col gauche : breadcrumb */}
          <div className="pro-header__breadcrumb">
            <span className="pro-header__breadcrumb-item pro-header__breadcrumb-item--main">{t('Tableau de bord')}</span>
            {urlSearch && (
              <>
                <span className="pro-header__breadcrumb-separator">›</span>
                <span className="pro-header__breadcrumb-item pro-header__breadcrumb-item--site">
                  {(() => {
                    const toDomain = (u) =>
                      (u || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();
                    return toDomain(urlSearch);
                  })()}
                </span>
              </>
            )}
          </div>


          <div className="pro-header__actions">
            {/* Sélecteur de site avec icône de base de données */}
            <div className="pro-header__picker">
              <div className="pro-header__site-selector">
                <svg
                  className="pro-header__db-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M2 4C2 3.44772 2.44772 3 3 3H13C13.5523 3 14 3.44772 14 4V12C14 12.5523 13.5523 13 13 13H3C2.44772 13 2 12.5523 2 12V4Z" stroke="#666" strokeWidth="1.5" fill="none" />
                  <path d="M2 6H14" stroke="#666" strokeWidth="1.5" />
                  <path d="M2 9H14" stroke="#666" strokeWidth="1.5" />
                </svg>
                <div className="pro-header__filter-wrapper">
                  <WebsiteFilter
                    websitesList={clients}
                    selectedWebsiteUrl={urlSearch}
                    setSelectedWebsiteUrl={setUrlSearch}
                    onAddWebsite={handleAddWebsite}
                    onEditWebsite={handleEditWebsite}
                    onDeleteWebsite={handleDeleteWebsite}
                    storageKey="AC_Dashboard_SelectedWebsite"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <hr className="pro-header__divider" />
      <div className="cards-row">
        <div className="Performances">
          <h2 className="section-title" style={{ marginBottom: '20px' }}>
            {t('Status de validation des projets')}
          </h2>

          <div className="donut-row">
            <div className="donut-chart-container">
              <div className="donut-chart"
                onMouseLeave={() => setDonutTooltip({ visible: false, label: '', x: 0, y: 0 })}
              >
                {donutTooltip.visible && (
                  <div
                    className="donut-custom-tooltip"
                    style={{
                      left: donutTooltip.x,
                      top: donutTooltip.y,
                    }}
                  >
                    {donutTooltip.label}
                  </div>
                )}
                <svg
                  className="donut-svg"
                  viewBox="0 0 120 120"
                >
                  <circle
                    className="donut-bg"
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="transparent"
                    stroke="#F3F4F6"
                    strokeWidth="12"
                  />
                  {(() => {
                    const segmentsWithOffsets = segments.reduce((acc, seg) => {
                      const offset = acc.currentOffset;
                      acc.segments.push({
                        ...seg,
                        offset,
                        dashArray: `${seg.length} ${circumference - seg.length}`,
                      });
                      acc.currentOffset += seg.length;
                      return acc;
                    }, { currentOffset: 0, segments: [] }).segments;


                    const reversedSegments = [...segmentsWithOffsets].reverse();

                    const percentages = [
                      { pct: uPct, label: t('Non attribué'), color: '#6458F1' },
                      { pct: pPct, label: t('En cours'), color: '#FF855D' },
                      { pct: dPct, label: t('En retard'), color: '#C06DD8' },
                      { pct: vPct, label: t('Validé'), color: '#63D3D7' }
                    ].filter(item => item.pct > 0);

                    const colorToLabelMap = {
                      '#6458F1': t('Non attribué'),
                      '#FF855D': t('En cours'),
                      '#C06DD8': t('En retard'),
                      '#63D3D7': t('Validé'),
                    };

                    return (
                      <>
                        {reversedSegments.map((seg, reversedIndex) => {
                          const label = colorToLabelMap[seg.color] || '';

                          return (
                            <circle
                              key={`circle-${reversedIndex}`}
                              cx="60"
                              cy="60"
                              r={radius}
                              fill="transparent"
                              stroke={seg.color}
                              strokeWidth="12"
                              strokeLinecap="round"
                              strokeDasharray={seg.dashArray}
                              strokeDashoffset={-seg.offset}
                              onMouseMove={(e) => {
                                const svg = e.currentTarget.closest('svg');
                                const containerRect = e.currentTarget.closest('.donut-chart').getBoundingClientRect();


                                const svgPoint = svg.createSVGPoint();
                                svgPoint.x = e.clientX;
                                svgPoint.y = e.clientY;
                                const svgCTM = svg.getScreenCTM();
                                if (svgCTM) {
                                  const pointTransformed = svgPoint.matrixTransform(svgCTM.inverse());


                                  const centerX = 60;
                                  const centerY = 60;
                                  const distanceFromCenter = Math.sqrt(
                                    Math.pow(pointTransformed.x - centerX, 2) +
                                    Math.pow(pointTransformed.y - centerY, 2)
                                  );


                                  const innerRadius = 48;
                                  const outerRadius = 54;

                                  if (distanceFromCenter < innerRadius || distanceFromCenter > outerRadius + 2) {
                                    setDonutTooltip({ visible: false, label: '', x: 0, y: 0 });
                                    return;
                                  }
                                }

                                const mouseX = e.clientX - containerRect.left;
                                const mouseY = e.clientY - containerRect.top;
                                setDonutTooltip({
                                  visible: true,
                                  label: label,
                                  x: mouseX,
                                  y: mouseY
                                });
                              }}
                              onMouseEnter={(e) => {
                                const svg = e.currentTarget.closest('svg');
                                const containerRect = e.currentTarget.closest('.donut-chart').getBoundingClientRect();

                                const svgPoint = svg.createSVGPoint();
                                svgPoint.x = e.clientX;
                                svgPoint.y = e.clientY;
                                const svgCTM = svg.getScreenCTM();
                                if (svgCTM) {
                                  const pointTransformed = svgPoint.matrixTransform(svgCTM.inverse());

                                  const centerX = 60;
                                  const centerY = 60;
                                  const distanceFromCenter = Math.sqrt(
                                    Math.pow(pointTransformed.x - centerX, 2) +
                                    Math.pow(pointTransformed.y - centerY, 2)
                                  );


                                  const innerRadius = 48;
                                  const outerRadius = 54;

                                  if (distanceFromCenter < innerRadius || distanceFromCenter > outerRadius + 2) {
                                    return;
                                  }
                                }

                                const mouseX = e.clientX - containerRect.left;
                                const mouseY = e.clientY - containerRect.top;
                                setDonutTooltip({
                                  visible: true,
                                  label: label,
                                  x: mouseX,
                                  y: mouseY
                                });
                              }}
                              onMouseLeave={() => {
                                setDonutTooltip({ visible: false, label: '', x: 0, y: 0 });
                              }}
                            />
                          );
                        })}
                        {/* Pour supprimer l'affichage des pourcentages dans le donut,
                              on retire simplement le rendu du texte à l'intérieur des segments. */}

                        <circle
                          cx="60"
                          cy="60"
                          r="40"
                          fill="white"
                          fillOpacity="0"
                          style={{ pointerEvents: 'all', cursor: 'default' }}
                          onMouseEnter={() => setDonutTooltip({ visible: false, label: '', x: 0, y: 0 })}
                          onMouseMove={() => setDonutTooltip({ visible: false, label: '', x: 0, y: 0 })}
                        />
                      </>
                    );
                  })()}



                  <text
                    x="60"
                    y="60"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      fill: '#111827',
                      pointerEvents: 'none',
                    }}
                  >
                    {`${totalProjects || 0} ${totalProjects > 1 ? t('projets') : t('projet')
                      }`}
                  </text>
                </svg>
              </div>
            </div>

            <div className="legend-container">
              <div className="legend-item">
                <div className="legend-dot yellow-dot"></div>
                <span className="legend-text">
                  <span style={{ color: '#6458F1', fontWeight: 600, marginRight: 4 }}>
                    {projectStats.numberOfProjectUnassigned || 0}
                  </span>
                  {t('Non attribué')}
                </span>
              </div>

              <div className="legend-item">
                <div className="legend-dot blue-dot"></div>
                <span className="legend-text">
                  <span style={{ color: '#FF855D', fontWeight: 600, marginRight: 4 }}>
                    {projectStats.numberOfProjectInProgress || 0}
                  </span>
                  {t('En cours')}
                </span>
              </div>

              <div className="legend-item">
                <div className="legend-dot purple-dot"></div>
                <span className="legend-text">
                  <span style={{ color: '#C06DD8', fontWeight: 600, marginRight: 4 }}>
                    {projectStats.numberOfProjectInDelay || 0}
                  </span>
                  {t('En retard')}
                </span>
              </div>

              <div className="legend-item">
                <div className="legend-dot green-dot"></div>
                <span className="legend-text">
                  <span style={{ color: '#63D3D7', fontWeight: 600, marginRight: 4 }}>
                    {projectStats.numberOfProjectValidated || 0}
                  </span>
                  {t('Validé')}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="Performances second-card">

          <h2 className="section-title" style={{ marginBottom: '8px' }}>
            {t('Statut d\'avancement des contenus')}
          </h2>

          <div style={{ marginBottom: '15px', fontSize: '14px', fontWeight: 500, color: '#3c3c3c' }}>
            {t('Total des contenus')}  <span style={{ fontWeight: 700 }}>{contentStats.totalNumberOfText || 0}</span>
          </div>
          <div className="bar-chart-container">
            <div className="bars-wrapper">
              <div className="y-axis-label">
                <span className="y-axis-text">{t('Pourcentage')}</span>
              </div>

              <div className="bars-container">
                <span className="y-axis-max">100</span>
                <span className="y-axis-min">0</span>

                {/* Lignes horizontales de repère */}
                <div className="bar-grid">
                  <div className="bar-grid-line" style={{ top: '5%' }} />
                  <div className="bar-grid-line" style={{ top: '25%' }} />
                  <div className="bar-grid-line" style={{ top: '50%' }} />
                  <div className="bar-grid-line" style={{ top: '75%' }} />
                  <div className="bar-grid-line" style={{ top: '100%' }} />
                </div>

                {(() => {
                  const count = contentStats.numberOfTextInDraft || 0;
                  const pct = urlSearch && contentStats.totalNumberOfText > 0
                    ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                    : 0;
                  const isTiny = pct < 12 && pct > 0;
                  const hasValue = count > 0 && pct > 0;
                  const isZero = count === 0;
                  return (
                    <div className="bar-item">
                      <div
                        className={`bar red-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                        style={{
                          height: hasValue ? `${pct}%` : '4px',
                          background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                          boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                        }}
                      >
                        {hasValue && <div className="bar-percentage">{count}</div>}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const count = contentStats.numberOfTextDelivered || 0;
                  const pct = urlSearch && contentStats.totalNumberOfText > 0
                    ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                    : 0;
                  const isTiny = pct < 12 && pct > 0;
                  const hasValue = count > 0 && pct > 0;
                  const isZero = count === 0;
                  return (
                    <div className="bar-item">
                      <div
                        className={`bar green-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                        style={{
                          height: hasValue ? `${pct}%` : '4px',
                          background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                          boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                        }}
                      >
                        {hasValue && <div className="bar-percentage">{count}</div>}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const count = contentStats.numberOfTextInRevision || 0;
                  const pct = urlSearch && contentStats.totalNumberOfText > 0
                    ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                    : 0;
                  const isTiny = pct < 12 && pct > 0;
                  const hasValue = count > 0 && pct > 0;
                  const isZero = count === 0;
                  return (
                    <div className="bar-item">
                      <div
                        className={`bar revision-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                        style={{
                          height: hasValue ? `${pct}%` : '4px',
                          background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                          boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                        }}
                      >
                        {hasValue && <div className="bar-percentage">{count}</div>}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const count = contentStats.numberOfTextValidated || 0;
                  const pct = urlSearch && contentStats.totalNumberOfText > 0
                    ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                    : 0;
                  const isTiny = pct < 12 && pct > 0;
                  const hasValue = count > 0 && pct > 0;
                  const isZero = count === 0;
                  return (
                    <div className="bar-item">
                      <div
                        className={`bar blue-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                        style={{
                          height: hasValue ? `${pct}%` : '4px',
                          background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                          boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                        }}
                      >
                        {hasValue && <div className="bar-percentage">{count}</div>}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const count = contentStats.numberOfTextDeployed || 0;
                  const pct = urlSearch && contentStats.totalNumberOfText > 0
                    ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                    : 0;
                  const isTiny = pct < 12 && pct > 0;
                  const hasValue = count > 0 && pct > 0;
                  const isZero = count === 0;
                  return (
                    <div className="bar-item">
                      <div
                        className={`bar cyan-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                        style={{
                          height: hasValue ? `${pct}%` : '4px',
                          background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                          boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                        }}
                      >
                        {hasValue && <div className="bar-percentage">{count}</div>}
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>

          <div className="status-legend">
            <div className="legend-item">
              <span className="legend-dot yellow-dot"></span>
              <span className="legend-text">{t('Brouillon')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot blue-dot"></span>
              <span className="legend-text">{t('Livré')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot revision-dot"></span>
              <span className="legend-text">{t('Révision')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot purple-dot"></span>
              <span className="legend-text">{t('Validé')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot green-dot"></span>
              <span className="legend-text">{t('Publié')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Nombre de textes publiés sous les deux blocs de statuts */}
      <div className="full-width-card" id="monthly-stat">

        <div className="card-header">
          <h2 className="section-title">
            {t('Nombre de textes publiés')}
          </h2>
          <div className="year-filter-container">
            <button
              ref={yearButtonRef}
              className="year-filter-button"
              onClick={(e) => {
                e.stopPropagation();
                if (yearDropdownRef.current) {
                  yearDropdownRef.current.classList.toggle('year-dropdown-open');
                }
              }}
            >
              <img
                src="/Images/icon-date.png"
                alt="date icon"
                className="year-filter-icon"
                style={{ width: '16px', height: '16px' }}
              />
              <span className="year-filter-text">{selectedYear}</span>
              <svg
                className="year-filter-arrow"
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div ref={yearDropdownRef} className="year-dropdown">
              {availableYears.length > 0 ? (
                availableYears.map((year) => (
                  <button
                    key={year}
                    className={`year-dropdown-item ${selectedYear === year ? 'year-dropdown-item--active' : ''}`}
                    onClick={() => {
                      setSelectedYear(year);
                      if (yearDropdownRef.current) {
                        yearDropdownRef.current.classList.remove('year-dropdown-open');
                      }
                    }}
                  >
                    {year}
                  </button>
                ))
              ) : (
                <button
                  className="year-dropdown-item"
                  disabled
                >
                  {t('Aucune donnée')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card-content">
          <div className="monthly-chart-container">
            <div className="monthly-bars-wrapper">
              <div className="monthly-bars-container">

                <div className="bar-grid">
                  <div className="bar-grid-line" style={{ top: '15%' }} />
                  <div className="bar-grid-line" style={{ top: '35%' }} />
                  <div className="bar-grid-line" style={{ top: '55%' }} />
                  <div className="bar-grid-line" style={{ top: '75%' }} />
                  <div className="bar-grid-line" style={{ top: '93%' }} />
                </div>
                {(() => {
                  const monthColors = {
                    january: 'rgba(224, 228, 255, 0.75)',
                    february: 'rgba(199, 205, 254, 0.75)',
                    march: 'rgba(199, 205, 254, 0.75)',
                    april: 'rgba(224, 228, 255, 0.75)',
                    may: 'rgba(238, 240, 255, 0.75)',
                    june: 'rgba(238, 240, 255, 0.75)',
                    july: 'rgba(224, 228, 255, 0.75)',
                    august: 'rgba(100, 88, 241, 0.75)',
                    september: 'rgba(224, 228, 255, 0.75)',
                    october: 'rgba(224, 228, 255, 0.75)',
                    november: 'rgba(224, 228, 255, 0.75)',
                    december: 'rgba(199, 205, 254, 0.75)',
                  };

                  const createGradient = (rgbaColor) => {
                    // Extraire les valeurs RGB de la chaîne rgba(r, g, b, a)
                    const match = rgbaColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                    if (match) {
                      const r = match[1];
                      const g = match[2];
                      const b = match[3];
                      return `linear-gradient(to top, rgba(${r}, ${g}, ${b}, 0.65) 0%, rgba(${r}, ${g}, ${b}, 1) 45%)`;
                    }
                    return rgbaColor; // Fallback si le parsing échoue
                  };

                  const months = [
                    { key: 'january', label: t('Jan') },
                    { key: 'february', label: t('Fév') },
                    { key: 'march', label: t('Mars') },
                    { key: 'april', label: t('Avr') },
                    { key: 'may', label: t('Mai') },
                    { key: 'june', label: t('Juin') },
                    { key: 'july', label: t('Juil') },
                    { key: 'august', label: t('Août') },
                    { key: 'september', label: t('Sept') },
                    { key: 'october', label: t('Oct') },
                    { key: 'november', label: t('Nov') },
                    { key: 'december', label: t('Déc') },
                  ];

                  // Filtrer les données par année sélectionnée
                  const deployedForYear = monthlyTextsStats?.deployed?.[selectedYear] || {};
                  const max = Math.max(1, ...months.map(m => Number(deployedForYear[m.key] || 0)));

                  return months.map((m) => {
                    const value = Number(deployedForYear[m.key] || 0);
                    const height = Math.round((value / max) * 120);
                    return (
                      <div key={m.key} className="month-bar-item">
                        <div className="month-value">{value}</div>
                        <div className="month-bar" style={{ height: `${height}px`, background: createGradient(monthColors[m.key]) }}></div>
                        <div className="month-label">{m.label}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="main-content-layout">

        <div className="left-column">
          <TrafficPerformanceChart urlSearch={urlSearch} seoConfig={seoConfig} />
        </div>
        <div className="right-column">
          <div className="content-section" id="latest-contents-section" style={{ marginTop: '-5px' }}>
            <div className="section-header latest-contents-header">
              <h2 className="section-title">{t('Derniers contenus ajoutés')}</h2>
              <button
                className="latest-contents-arrow"
                onClick={() => history.push('/AdminClient/Listofwebsitetexts')}
                aria-label={t('Voir plus')}
              >
                <span className="latest-contents-arrow-text">{t('Voir plus')}</span>
                <img
                  src="/Images/show-more.png"
                  alt={t('Voir plus')}
                  className="latest-contents-arrow-icon"
                />
              </button>
            </div>

            <div className="performance-table-wrapper" style={{ marginTop: '15px' }}>
              <table className="performance-table">
                <thead>
                  <tr>
                    <th className="col-keyword"> {t('Texte')}</th>
                    <th style={{ textAlign: 'center' }}> {t('Statut')}</th>
                    <th> {t('Date d’ajout')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingContents ? (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {t('Chargement...')}
                      </td>
                    </tr>
                  ) : latestContents.length > 0 ? (
                    <>
                      {latestContents.slice(0, 5).map((content, idx) => {
                        return (
                          <tr
                            key={content.id || idx}
                            onClick={() => openTextDetails(content)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="keyword-cell">
                              {content.keyword || t('Sans titre')}
                            </td>
                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                              <div
                                style={{
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: '6px',
                                  position: 'relative',
                                }}
                              >
                                <img
                                  src={getTextStatusIcon(content)}
                                  alt={getTextStatusLabel(content)}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    display: 'inline-block',
                                    flexShrink: 0
                                  }}
                                />
                                <span style={{ fontSize: '12px', fontWeight: 500 }}>{getTextStatusLabel(content)}</span>
                              </div>
                            </td>
                            <td>
                              {content.createdAt
                                ? formatDateShort(content.createdAt)
                                : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  ) : (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {urlSearch ? t('Aucun contenu disponible pour ce site web.') : t('Sélectionnez un site web pour voir les contenus.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="content-section" id="latest-project-section">
            <div className="section-header latest-contents-header">
              <h2 className="section-title">{t('Derniers projets crées')}</h2>
              <button
                className="latest-contents-arrow"
                onClick={() => history.push('/AdminClient/ProjectsEnCours')}
                aria-label={t('Voir plus')}
              >
                <span className="latest-contents-arrow-text">{t('Voir plus')}</span>
                <img
                  src="/Images/show-more.png"
                  alt={t('Voir plus')}
                  className="latest-contents-arrow-icon"
                />
              </button>
            </div>

            <div className="performance-table-wrapper" style={{ marginTop: '15px' }}>
              <table className="performance-table">
                <thead>
                  <tr>
                    <th className="col-keyword" style={{ maxWidth: 160, width: 160 }}>{t('Titre')}</th>
                    <th style={{ textAlign: 'center' }}>{t('Statut')}</th>
                    <th>{t('Date d’ajout')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingProjects ? (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {t('Chargement...')}
                      </td>
                    </tr>
                  ) : latestProjects.length > 0 ? (
                    latestProjects.slice(0, 5).map((project, idx) => (
                      <tr
                        key={project.id || idx}
                        onClick={() => openProjectDetails(project)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="keyword-cell" style={{ maxWidth: 180, width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.title ||
                            project.titre ||
                            project.name ||
                            project.projectName ||
                            t('Sans titre')}
                        </td>
                        <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                          {(() => {
                            const status = project.status;
                            if (!status) return '-';
                            return (
                              <div
                                style={{
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: '6px',
                                  position: 'relative',
                                }}
                              >
                                <img
                                  src={getProjectStatusIcon(project)}
                                  alt={getProjectStatusLabel(project)}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    display: 'inline-block',
                                    flexShrink: 0
                                  }}
                                />
                                <span style={{ fontSize: '12px', fontWeight: 500 }}>{getProjectStatusLabel(project)}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {project.createdAt ||
                            project.dateCreation ||
                            project.creationDate
                            ? formatDateShort(
                              project.createdAt ||
                              project.dateCreation ||
                              project.creationDate
                            )
                            : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {urlSearch ? (
                          <>
                            {t('Aucun projet créé pour le moment.')}{' '}
                            <span
                              className="project-link"
                              onClick={() => history.push('/AdminClient/CreateProject')}
                              style={{ cursor: 'pointer' }}
                            >
                              {t('Lancer votre premier projet')}
                            </span>
                          </>
                        ) : (
                          t('Sélectionnez un site web pour voir les projets.')
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-section no-border" id="top-ranked-section">
            <div className="performance-top-section" id="topRanked">
              <div className="section-header latest-contents-header" style={{ marginBottom: '6px' }}>
                <h2 className="section-title">{t('Top ranked')}</h2>
              </div>
              <div className="performance-table-wrapper">
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th className="col-keyword">{t('Mot clé')}</th>
                      <th>{t('Position')}</th>
                      <th>{t('Clicks')}</th>
                      <th>{t('Clicks (Y-1)')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingTopContents ? (
                      <tr style={{ background: 'transparent' }}>
                        <td
                          className="keyword-cell"
                          colSpan={4}
                          style={{
                            textAlign: 'center',
                            color: '#666',
                            background: 'transparent',
                            border: 'none'
                          }}
                        >
                          {t('Chargement...')}
                        </td>
                      </tr>
                    ) : topContentsData && topContentsData.length > 0 ? (
                      topContentsData.slice(0, 5).map((row, idx) => (
                        <tr key={idx}>
                          <td className="keyword-cell">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: '#5137CB',
                                  color: '#FFFFFF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                {idx + 1}
                              </div>
                              <span>{row.query || '-'}</span>
                            </div>
                          </td>
                          <td style={{ fontWeight: 600 }}>{row.currentPosition || '-'}</td>
                          <td style={{ fontWeight: 600 }}>{row.currentValue !== null && row.currentValue !== undefined ? row.currentValue : '-'}</td>
                          <td style={{ fontWeight: 600 }}>{row.previousValue !== null && row.previousValue !== undefined ? row.previousValue : '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr style={{ background: 'transparent' }}>
                        <td colSpan={4} style={{ background: 'transparent', border: 'none', position: 'relative' }}>
                          <div className="chart-empty-wrapper" style={{ height: 80 }}>
                            <div className="empty-overlay" style={{ filter: 'none' }}>
                              <div className="overlay-card" style={{ maxWidth: 680, boxShadow: 'none' }}>
                                <span>
                                  {t('Pour activer cette fonctionnalité, ajoutez notre adresse ')}

                                  <strong style={{ color: '#000' }}>typix@gmail.com</strong>
                                  {t(' à votre ')}
                                  <strong style={{ color: '#000' }}>{t('Search Console')}</strong>
                                  {t(', ')}
                                  <a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#0064d2', textDecoration: 'underline' }}>{t('puis cliquez ici pour lancer l’analyse.')}</a>
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => {
          setConfirmation(false);
          setIsDeleteWithProjectsFlow(false);
        }}
        executeAction={isDeleteWithProjectsFlow ? deleteWebsiteWithProjects : deleteWebsite}
      />
      <QuitProjectCreationModal
        isOpen={isWebsiteLimitModalOpen}
        onClose={() => setIsWebsiteLimitModalOpen(false)}
        variant="enrichment"
        showCloseButton={false}
        titleIcon="/Images/black-magic-icon.png"
        title={t('Limite de sites atteinte')}
        message={
          isFreePlan ? (
            <>
              {t('Vous avez atteint le nombre maximum de sites web autorisés.')}
              {' '}
              <strong style={{ color: 'black' }}>{t('Passez à une offre supérieure')}</strong>
              {' '}
              {t('pour')}
              <strong style={{ color: 'black' }}>{t(' ajouter de nouveaux sites et')}</strong>
              {' '}
              {t('étendre votre activité')}
              .
            </>
          ) : (
            <>
              {t('Vous avez atteint le nombre maximum de sites web autorisés.')}
              <br />
              <br />
              {t('Veuillez mettre à niveau votre offre actuelle pour ajouter un nouveau site.')}
            </>
          )
        }
        showSuggestion={false}
        primaryButtonLabel={isFreePlan ? t('Voir les offres') : t('Mettre à niveau')}
        secondaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => {
          setIsWebsiteLimitModalOpen(false);
          if (isFreePlan) {
            history.push('/AdminClient/Forfait');
            return;
          }
          history.push('/AdminClient/Abonnement?openManageSubscription=1');
        }}
        onSecondaryAction={() => setIsWebsiteLimitModalOpen(false)}
      />
    </div>
  );
};

